import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import type { Page } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { LoadFailed, NoMatches } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { DataTable } from "@/components/table/DataTable";
import { Pagination } from "@/components/table/Pagination";
import { useCursorHistory } from "@/components/table/useCursorHistory";
import { Badge } from "@/components/ui/badge";
import { matchesFilter } from "@/lib/filter-predicate";
import { reportingNow } from "@/lib/reportClock";
import { cn } from "@/lib/utils";
import {
  ageSeverity,
  deriveInTransitLines,
  sortInTransitLines,
  type AgeSeverity,
  type InTransitLine,
} from "@/modules/warehouses/in-transit";
import { projectInTransitLine } from "@/modules/warehouses/projections";
import type { SectionScreenProps } from "@/modules/types";

const AGE_BADGE_CLASS: Record<AgeSeverity, string> = {
  fresh: "",
  aging: "border-warning/60 bg-warning/10 text-warning",
  stale: "border-destructive/60 bg-destructive/10 text-destructive",
};

/**
 * FR-WHS-12: every in-transit line, with its age, so a transfer dispatched
 * and never received becomes visible instead of lost.
 *
 * There is no repository call shaped like this row — `WarehousesRepository`
 * has no method that flattens a transfer's lines, because nothing in
 * Shopify has a concept of "in transit" for this to mirror. The screen
 * fetches transfers and warehouses through the sanctioned interface and
 * derives, filters, sorts and pages the rows itself from data the interface
 * already returned, rather than reaching past it.
 */
export function InTransit({
  fields,
  filter,
  sort,
  onToggleSort,
  onClearFilter,
}: SectionScreenProps) {
  const data = useDataSource();

  const [limit, setLimit] = useState(25);
  const [history, setHistory] = useCursorHistory();
  const cursor = history[history.length - 1];
  const start = cursor ? Number(cursor) : 0;

  const warehousesQuery = useQuery({
    queryKey: ["warehouses", "list-all"],
    queryFn: () => data.warehouses.list({ limit: 200 }),
  });

  const transfersQuery = useQuery({
    // Unfiltered and unpaged at the repository: the rows this screen shows
    // are lines, not transfers, so a transfer-level cursor would page the
    // wrong thing. Every transfer is fetched once and the outstanding,
    // line-level view is derived below.
    queryKey: ["warehouses", "transfers", "all-for-in-transit"],
    queryFn: () => data.warehouses.listTransfers({ limit: 200 }),
  });

  if (warehousesQuery.isPending || transfersQuery.isPending) {
    return <TableSkeleton />;
  }

  if (warehousesQuery.isError) {
    return (
      <LoadFailed
        message={warehousesQuery.error.message}
        onRetry={() => {
          void warehousesQuery.refetch();
        }}
      />
    );
  }

  if (transfersQuery.isError) {
    return (
      <LoadFailed
        message={transfersQuery.error.message}
        onRetry={() => {
          void transfersQuery.refetch();
        }}
      />
    );
  }

  const nameById = new Map(
    warehousesQuery.data.items.map((warehouse) => [
      warehouse.id,
      warehouse.name,
    ]),
  );
  const warehouseName = (id: string) => nameById.get(id) ?? id;

  const allLines = deriveInTransitLines(
    transfersQuery.data.items,
    warehouseName,
  );
  // The filter builder still writes to this screen's URL like any other —
  // it is applied here, against the derived rows, because there is no
  // repository list this screen's rows come from for it to be forwarded to.
  const matching = allLines.filter((line) =>
    matchesFilter(projectInTransitLine(line), filter, reportingNow()),
  );
  const sorted = sortInTransitLines(matching, sort, projectInTransitLine);

  const items = sorted.slice(start, start + limit);
  const nextStart = start + limit;
  const page: Page<InTransitLine> = {
    items,
    nextCursor: nextStart < sorted.length ? String(nextStart) : null,
    total: sorted.length,
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <DataTable
        fields={fields}
        rows={items}
        getRowId={(line) => line.id}
        project={projectInTransitLine}
        sort={sort}
        onToggleSort={onToggleSort}
        renderOverride={(field, line) => {
          if (field.name !== "ageDays") return undefined;
          const severity = ageSeverity(line.ageDays);
          return (
            <Badge
              variant="outline"
              className={cn(
                "font-normal tabular-nums",
                AGE_BADGE_CLASS[severity],
              )}
            >
              {line.ageDays}d
            </Badge>
          );
        }}
        emptyState={<NoMatches onClear={onClearFilter} />}
        footer={
          <Pagination
            page={page}
            limit={limit}
            onLimitChange={(next) => {
              setLimit(next);
              setHistory([undefined]);
            }}
            history={history}
            onNavigate={setHistory}
          />
        }
      />
    </div>
  );
}
